import { useState, useEffect } from "react";
import styles from "./index.module.scss";
import classNames from "classnames/bind";
import { db } from "../../../firebase";
import { collection, addDoc } from "firebase/firestore";
import { getRandomAd, AdContent } from "./adData";

const cx = classNames.bind(styles);

interface GameOverModalProps {
  isVisible: boolean;
  onClick: () => void;
  score: number;
  isInstagramModalVisible: boolean;
  setIsInstagramModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
  playerName: string;
  setPlayerName: React.Dispatch<React.SetStateAction<string>>;
  bestScore: number;
  onClose?: () => void;
  isNewGame?: boolean; // 새 게임인지 구분
}

let timeout: NodeJS.Timeout | null = null;

const GameOverModal = ({
  isVisible,
  onClick,
  score,
  playerName,
  setPlayerName,
  bestScore,
  onClose,
  isNewGame = false,
}: GameOverModalProps) => {
  const [toastVisible, setToastVisible] = useState(false);
  const [isRankingSubmitted, setIsRankingSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [adCountdown, setAdCountdown] = useState(5);
  const [showAd, setShowAd] = useState(true);
  const [currentAd, setCurrentAd] = useState<AdContent | null>(null);

  // 5초 광고 카운트다운 (새 게임일 때만)
  useEffect(() => {
    if (!isVisible) return;

    if (isNewGame) {
      // 새 게임일 때만 광고 표시
      setAdCountdown(5);
      setShowAd(true);
      setCurrentAd(getRandomAd()); // 랜덤 광고 선택

      const timer = setInterval(() => {
        setAdCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    } else {
      // 기존 게임 재개일 때는 바로 일반 화면 표시
      setShowAd(false);
    }
  }, [isVisible, isNewGame]);

  if (!isVisible) return null;

  const share = () => {
    if (navigator.share) {
      navigator
        .share({
          title: "공공장장의 순원볼 게임",
          text: "도자기들을 모아 막걸리잔을 만들어보세요.",
          url: "https://yido-rho.vercel.app/",
        })
        .then(() => console.log("done"))
        .catch((error) => console.log(error));
    } else {
      timeout && clearTimeout(timeout);
      const urlToCopy = window.location.href;

      if (document.queryCommandSupported("copy")) {
        const input = document.createElement("input");
        input.value = urlToCopy;
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        document.body.removeChild(input);
      } else {
        navigator.clipboard.writeText(urlToCopy);
      }

      setToastVisible(true);
      timeout = setTimeout(() => {
        setToastVisible(false);
      }, 2800);
    }
  };


  const handleRankingSubmit = async () => {
    if (playerName.trim() && !isSubmitting) {
      try {
        setIsSubmitting(true);
        await addDoc(collection(db, "rankings"), {
          name: playerName.trim(),
          score: score,
          createdAt: new Date(),
        });
        console.log("랭킹이 성공적으로 저장되었습니다!");
        setIsRankingSubmitted(true);
        setIsSubmitting(false);
      } catch (error) {
        console.error("랭킹 저장 중 오류 발생:", error);
        alert("랭킹 저장에 실패했습니다. 다시 시도해주세요.");
        setIsSubmitting(false);
      }
    }
  };

  const handleTryAgain = () => {
    setIsRankingSubmitted(false);
    onClick();
  };

  const handleCloseAd = () => {
    setShowAd(false);
  };

  return (
    <div className={cx("gameOverArea")}>
      {!showAd && (
        <button
          className={cx("closeBtn")}
          onClick={() => onClose && onClose()}
          aria-label="닫기"
        >
          ×
        </button>
      )}

      {showAd && currentAd ? (
        <div
          className={cx("adArea")}
          style={{ background: currentAd.backgroundGradient }}
        >
          <div className={cx("adContent")}>
            <h2 className={cx("adTitle")}>{currentAd.title}</h2>
            <p className={cx("adText")}>
              {currentAd.description}
            </p>
            <div
              className={cx("countdown", { clickable: adCountdown === 0 })}
              onClick={adCountdown === 0 ? handleCloseAd : undefined}
              style={{ cursor: adCountdown === 0 ? 'pointer' : 'default' }}
            >
              {adCountdown > 0 ? adCountdown : "×"}
            </div>
          </div>
        </div>
      ) : (
        <>
          <span className={cx("text")}>GAME OVER</span>
          <span className={cx("score")}>SCORE: {score}</span>

          <div className={cx("nameInput")}>
            <input
              type="text"
              value={playerName}
              onChange={(e) => setPlayerName(e.target.value)}
              placeholder="이름을 입력하세요"
              className={cx("nameInputField")}
              disabled={isRankingSubmitted}
            />
            <button
              className={cx("rankingBtn", { submitted: isRankingSubmitted })}
              onClick={handleRankingSubmit}
              disabled={!playerName.trim() || isRankingSubmitted || isSubmitting}
            >
              {isSubmitting
                ? "등록중..."
                : isRankingSubmitted
                  ? "등록완료"
                  : "랭킹 등록"}
            </button>
          </div>

          <button className={cx("btn")} onClick={handleTryAgain}>
            ↻ TRY AGAIN?
          </button>

          <div className={cx("linkArea")}>
            <button className={cx("shareaBtn")} onClick={share}>
              공유하기
            </button>
            <a
              href="https://yidomall.com/product/선물포장-순-막걸리잔-세트-2p-2-colors/2858/?utm_source=game1&utm_medium=referral&utm_campaign=game1"
              target="_blank"
              rel="noopener noreferrer"
              className={cx("link")}
            >
              막걸리잔 구경가기
            </a>
          </div>
        </>
      )}

      <div className={cx("toastArea", { show: toastVisible })}>
        🍉URL이 복사되었습니다.
      </div>
    </div>
  );
};

export default GameOverModal;
